/**
 * Whether focusing this control brings a phone's keyboard up: a text field or
 * a textarea that can be typed in. A checkbox is an `<input>` too, and ticking
 * one is not typing. The shell's `data-typing` mark reads the same rule, so the
 * plus never steps aside for a control that opens no keyboard.
 */
export function opensKeyboard(node: Element | null): node is HTMLElement {
  return node instanceof HTMLElement &&
    node.matches('textarea:not(:disabled):not([readonly]), input:not(:disabled):not([readonly])') &&
    !(node instanceof HTMLInputElement &&
      ["button", "submit", "reset", "checkbox", "radio", "range", "color", "file", "hidden"].includes(node.type));
}

/**
 * The signed-in shell owns one instance. Forms keep ownership of focus and
 * drafts; this only supplies room and reveals an obscured focused field.
 */
export function keyboardViewport(root: HTMLElement, column: HTMLElement, phone: () => boolean) {
  const viewport = window.visualViewport;
  if (!viewport) return { reset() {}, dispose() {} };

  let pending = 0;
  let disposed = false;
  let field: HTMLElement | undefined;
  let yielded = false;
  const styled = new Map<HTMLElement, Map<string, string>>();
  let columnPadding: { inline: string; base: string; topInline: string; topBase: string } | undefined;

  function editable(node: Element | null): node is HTMLElement {
    return opensKeyboard(node) && root.contains(node);
  }

  function setStyle(node: HTMLElement, name: string, value: string) {
    let originals = styled.get(node);
    if (!originals) styled.set(node, originals = new Map());
    if (!originals.has(name)) originals.set(name, node.style.getPropertyValue(name));
    node.style.setProperty(name, value);
  }
  function restore(node: HTMLElement) {
    for (const [name, value] of styled.get(node) ?? []) {
      if (value) node.style.setProperty(name, value);
      else node.style.removeProperty(name);
    }
    styled.delete(node);
  }
  function restorePadding() {
    if (!columnPadding) return;
    column.style.paddingBottom = columnPadding.inline;
    column.style.paddingTop = columnPadding.topInline;
    columnPadding = undefined;
  }
  function restoreAll() {
    for (const node of styled.keys()) restore(node);
    restorePadding();
  }

  function update() {
    pending = 0;
    if (disposed) return;
    const focused = document.activeElement;
    if (editable(focused) && focused !== field) {
      field = focused;
      yielded = false;
    }
    if (!field?.isConnected || !root.contains(field)) field = undefined;

    const layoutHeight = document.documentElement.clientHeight || window.innerHeight;
    // Zoom is the browser's job. A reduced viewport alone does not identify a
    // keyboard; require an editing session too, and never infer a fixed height.
    if (!field || Math.abs(viewport!.scale - 1) > 0.01 || viewport!.height >= layoutHeight - 1) {
      restoreAll();
      return;
    }
    const top = viewport!.offsetTop;
    const bottom = top + viewport!.height;
    const inset = Math.max(0, layoutHeight - bottom);
    const dialogs = [...root.querySelectorAll<HTMLDialogElement>("dialog[open]")];
    for (const node of styled.keys()) if (!dialogs.includes(node as HTMLDialogElement)) restore(node);
    for (const dialog of dialogs) {
      setStyle(dialog, "position", "fixed");
      setStyle(dialog, "top", phone() ? "auto" : `${top}px`);
      setStyle(dialog, "bottom", `${inset}px`);
      // Keep the existing phone sheet's 90% ceiling within the visible area.
      setStyle(dialog, "max-height", `${viewport!.height * 0.9}px`);
    }

    const dialog = field.closest<HTMLDialogElement>("dialog[open]");
    if (!dialog) {
      columnPadding ??= {
        inline: column.style.paddingBottom,
        base: getComputedStyle(column).paddingBottom || "0px",
        topInline: column.style.paddingTop,
        topBase: getComputedStyle(column).paddingTop || "0px",
      };
      column.style.paddingBottom = `calc(${columnPadding.base} + ${inset}px)`;
      // At scrollY=0 an obscured heading cannot be scrolled any further up.
      // Leave room for the visual viewport's top offset as well as its bottom.
      column.style.paddingTop = `calc(${columnPadding.topBase} + ${top}px)`;
    } else restorePadding();

    // Bounds still follow the keyboard after manual scrolling. Only revealing
    // the field yields; otherwise a keyboard resize could swallow the sheet.
    if (yielded || focused !== field) return;
    const bounds = field.getBoundingClientRect();
    if (bounds.height <= 0) return;
    let visibleTop = top;
    let visibleBottom = bottom;
    // The docked inspector is a scroller of its own (g55): a field in it is
    // revealed by scrolling the inspector, within the inspector's box.
    const frame = dialog ?? field.closest<HTMLElement>(".inspector") ?? undefined;
    if (frame) {
      const box = frame.getBoundingClientRect();
      const header = frame.querySelector<HTMLElement>(".detail-head")?.getBoundingClientRect();
      visibleTop = Math.max(visibleTop, box.top, dialog ? header?.bottom ?? box.top : box.top);
      visibleBottom = Math.min(visibleBottom, box.bottom);
      // A footer that sticks to the bottom (g55) covers what scrolls under it.
      const footer = frame.querySelector<HTMLElement>(".editor-actions");
      if (footer && footer !== field.closest(".editor-actions") && getComputedStyle(footer).position === "sticky")
        visibleBottom = Math.min(visibleBottom, footer.getBoundingClientRect().top);
    }
    if (visibleBottom <= visibleTop) return;
    // A tall textarea cannot fit as a whole. While it overlaps the visible
    // area, leave native caret scrolling alone, including during input events.
    // Only bring it back if the entire field is outside that area.
    const tall = bounds.height > visibleBottom - visibleTop;
    const delta = tall
      ? bounds.top >= visibleBottom ? bounds.top - visibleTop
        : bounds.bottom <= visibleTop ? bounds.bottom - visibleBottom : 0
      : bounds.top < visibleTop ? bounds.top - visibleTop
        : Math.max(0, bounds.bottom - visibleBottom);
    if (Math.abs(delta) < 1) return;
    (frame ?? window).scrollBy({ top: delta, behavior: "instant" });
  }
  function schedule() {
    if (!disposed && !pending) pending = requestAnimationFrame(update);
  }
  function yieldToScroll() { yielded = true; }
  function wheel(event: WheelEvent) {
    if (event.deltaX || event.deltaY) yieldToScroll();
  }
  function key(event: KeyboardEvent) {
    // Arrows/Home/End in a field move the caret, not the page.
    if (!editable(event.target instanceof Element ? event.target : null) &&
      ["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) yieldToScroll();
  }
  function reset() {
    field = undefined;
    yielded = false;
    restoreAll();
    schedule();
  }

  root.addEventListener("focusin", schedule);
  root.addEventListener("focusout", schedule);
  root.addEventListener("input", schedule);
  root.addEventListener("close", schedule, true);
  // Scroll events do not identify their cause: native focus scrolling and our
  // own scrollBy must not cancel the later keyboard resize. Listen for intent.
  root.addEventListener("touchmove", yieldToScroll, { passive: true });
  root.addEventListener("wheel", wheel, { passive: true });
  root.addEventListener("keydown", key);
  viewport.addEventListener("resize", schedule);
  viewport.addEventListener("scroll", schedule);
  window.addEventListener("resize", schedule);

  return {
    reset,
    dispose() {
      disposed = true;
      cancelAnimationFrame(pending);
      restoreAll();
      root.removeEventListener("focusin", schedule);
      root.removeEventListener("focusout", schedule);
      root.removeEventListener("input", schedule);
      root.removeEventListener("close", schedule, true);
      root.removeEventListener("touchmove", yieldToScroll);
      root.removeEventListener("wheel", wheel);
      root.removeEventListener("keydown", key);
      viewport.removeEventListener("resize", schedule);
      viewport.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    },
  };
}
